import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { getReleaseDetails } from '../services/api';

interface ReleaseDetailsProps {
  lustId: string;
  releaseName: string;
  onClose: () => void;
}

const ReleaseDetails: React.FC<ReleaseDetailsProps> = ({ lustId, releaseName, onClose }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [details, setDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReleaseDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = await getAccessTokenSilently();
        const response = await getReleaseDetails(token, lustId);
        
        setDetails(response.details);
      } catch (err) {
        console.error('Failed to load release details:', err);
        setError('Failed to load release details');
      } finally {
        setLoading(false);
      }
    };

    loadReleaseDetails();
  }, [lustId, getAccessTokenSilently]);

  const formatValue = (key: string, value: any): string => {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (typeof value === 'number') {
      if (key.includes('Latitude') || key.includes('Longitude')) {
        return value.toFixed(6);
      }
      return value.toLocaleString();
    }
    
    return String(value);
  };

  const getDisplayName = (key: string): string => {
    const displayNames: { [key: string]: string } = {
      'LUST_ID': 'Release ID',
      'Name': 'Site Name',
      'Address': 'Address',
      'City': 'City',
      'County': 'County',
      'State': 'State',
      'Zip_Code': 'ZIP Code',
      'Latitude': 'Latitude',
      'Longitude': 'Longitude',
      'Coordinate_Source': 'Coordinate Source',
      'Address_Match_Type': 'Address Match Type',
      'Reported_Date': 'Reported Date',
      'Status': 'Status',
      'Substance': 'Substance Released',
      'Population_within_1500ft': 'Population within 1500ft',
      'DomesticWells_within_1500ft': 'Domestic Wells within 1500ft',
      'LandUse': 'Land Use',
      'Within_SPA': 'Within Source Protection Area',
      'SPA_PWS_FacilityID': 'SPA PWS Facility ID',
      'SPA_Water_Type': 'SPA Water Type',
      'SPA_Facility_Type': 'SPA Facility Type',
      'Within_WHPA': 'Within Wellhead Protection Area',
      'WHPA_PWS_FacilityID': 'WHPA PWS Facility ID',
      'WHPA_Water_Type': 'WHPA Water Type',
      'WHPA_Facility_Type': 'WHPA Facility Type',
      'Within_100yr_Floodplain': 'Within 100-Year Floodplain',
      'Tribe': 'Tribal Land',
      'EPA_Region': 'EPA Region',
      'Closed_With_Residual_Contaminat': 'Closed with Residual Contamination'
    };
    
    return displayNames[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const isImportantField = (key: string): boolean => {
    const importantFields = [
      'Name', 'Address', 'City', 'State', 'Zip_Code', 'County',
      'Status', 'Substance', 'Reported_Date',
      'Latitude', 'Longitude',
      'Population_within_1500ft', 'DomesticWells_within_1500ft',
      'Within_SPA', 'Within_WHPA', 'Within_100yr_Floodplain',
      'Closed_With_Residual_Contaminat'
    ];
    return importantFields.includes(key);
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div>Loading release details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <h3 style={{ color: '#ef4444', marginTop: 0 }}>Error</h3>
          <p>{error}</p>
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!details) {
    return null;
  }

  const importantFields = Object.keys(details).filter(isImportantField);
  const otherFields = Object.keys(details).filter(key => !isImportantField(key));

  // Determine status color
  const statusColor = details.Status === 'Open' ? '#ef4444' : 
                     details.Status === 'No Further Action' ? '#10b981' : '#f59e0b';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fef2f2'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#dc2626' }}>
              Release/Leak Details
            </h2>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#7f1d1d' }}>
              {releaseName} (ID: {lustId})
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.5rem',
              borderRadius: '4px'
            }}
          >
            ×
          </button>
        </div>
        
        <div style={{
          padding: '1.5rem',
          overflow: 'auto',
          flexGrow: 1
        }}>
          {/* Status Badge */}
          <div style={{ marginBottom: '1.5rem' }}>
            <span style={{
              display: 'inline-block',
              padding: '0.5rem 1rem',
              backgroundColor: statusColor,
              color: 'white',
              borderRadius: '4px',
              fontWeight: '600',
              fontSize: '1rem'
            }}>
              Status: {details.Status}
            </span>
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginTop: 0, color: '#1f2937', borderBottom: '2px solid #dc2626', paddingBottom: '0.5rem' }}>
              Key Information
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1rem',
              marginTop: '1rem'
            }}>
              {importantFields.map((key) => (
                <div key={key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '4px',
                  border: '1px solid #e5e7eb'
                }}>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    {getDisplayName(key)}:
                  </span>
                  <span style={{ 
                    fontWeight: (key === 'Status' && details[key] === 'Open') || 
                               (key === 'Within_SPA' && details[key] === 'Yes') ||
                               (key === 'Within_WHPA' && details[key] === 'Yes') ||
                               (key === 'Within_100yr_Floodplain' && details[key] === 'Yes') ? '600' : 'normal',
                    color: (key === 'Status' && details[key] === 'Open') ||
                          (key === 'Within_SPA' && details[key] === 'Yes') ||
                          (key === 'Within_WHPA' && details[key] === 'Yes') ||
                          (key === 'Within_100yr_Floodplain' && details[key] === 'Yes') ? '#ef4444' : '#1f2937'
                  }}>
                    {formatValue(key, details[key])}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {otherFields.length > 0 && (
            <div>
              <h3 style={{ color: '#1f2937', borderBottom: '2px solid #6b7280', paddingBottom: '0.5rem' }}>
                Additional Data Fields
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '0.5rem',
                marginTop: '1rem'
              }}>
                {otherFields.map((key) => (
                  <div key={key} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    <span style={{ fontWeight: '500', color: '#4b5563' }}>
                      {getDisplayName(key)}:
                    </span>
                    <span style={{ color: '#6b7280' }}>
                      {formatValue(key, details[key])}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <button onClick={onClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReleaseDetails;